import { useState, useEffect, useRef } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import Layout from "./Layout";
import usePageMeta from "../hooks/usePageMeta";
import { useAppPermissions } from "../context/PermissionContext";
import ActionDropdown from "../components/ActionDropdown";
import EditMessageForm from "../components/forms/editMessageForm";
import EditMessageTemplateForm from "../components/forms/EditMessageTemplateForm";
import MessageQueueForm from "../components/forms/MessageQueueForm"
import { FaCheckCircle, FaExclamationCircle, FaCheck } from "react-icons/fa"; 
import API from "../api";
import { showError, showConfirm, showSuccess } from '../utils/dialog';
import ComposeEmailModal from '../components/ComposeEmailModal';
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  AdminMessagesQueueFilterFields,
  buildAdminMessagesQueueFilterChips,
  AdminMessagesFilterFields,
  buildAdminMessagesFilterChips,
  QUEUE_SORT_OPTIONS,
  MESSAGES_SORT_OPTIONS,
} from "../components/filters";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";
import { formatAppIntlLabel } from "../utils/appDateTime";

// Sort arrow component
const SortArrow = ({ colKey, activeKey, activeDir, onChange }) => (
  <button
    onClick={() => onChange(colKey)}
    className="float-right mr-1 text-sm text-white/90 hover:text-white"
    title="Sort"
  >
    {activeKey === colKey ? (activeDir === 'asc' ? '▲' : '▼') : null}
  </button>
);

const Messages = () => {
  usePageMeta("Messages");
  const { canWriteAdmin } = useAppPermissions();
  const adminReadOnly = !canWriteAdmin;
  const filterPanel = useFilterPanel();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedTab, setSelectedTab] = useState("MESSAGE QUEUE");
  const [searchTerm, setSearchTerm] = usePersistentPageState(
    "page_search_messages",
    ""
  );
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [queueFilter, setQueueFilter] = usePersistentPageState(
    "page_queue_filter_messages",
    "all"
  );
  const [mqMessageType, setMqMessageType] = usePersistentPageState(
    "page_mq_type_messages",
    "all"
  );
  const [mqMessageTypeOptions, setMqMessageTypeOptions] = useState([
    "All",
    "Account Activation Request",
    "Account Invite Request",
    "Account Password Reset",
    "Admin Password Reset",
    "Audit Digest",
    "Confirm Availability",
    "Conversation Notification",
    "Password Changed",
    "Registrant Confirmation",
    "Registration: Digest Email",
    "Shift Actuals Reminder",
    "Shift Actuals Submitted",
    "Shift Signoff Reminder",
    "Sign in from a new device",
    "Temp Staff Request Change",
    "Verification Code",
  ]);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isTemplateFormOpen, setIsTemplateFormOpen] = useState(false);
  const [openFilterPanel, setOpenFilterPanel] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [mqDates, setMqDates] = usePersistentPageState(
    "page_dates_messages",
    { from: "", to: "" }
  );
  const mqFromDate = mqDates.from;
  const setMqFromDate = (val) => setMqDates((prev) => ({ ...prev, from: typeof val === "function" ? val(prev.from) : val }));
  const mqToDate = mqDates.to;
  const setMqToDate = (val) => setMqDates((prev) => ({ ...prev, to: typeof val === "function" ? val(prev.to) : val }));
  
  // Default sort fields for each tab
  const defaultSortFields = {
    "MESSAGE QUEUE": "create_date",
    "MESSAGES": "name",
    "MESSAGES TEMPLATE": "name",
    "WEBHOOKS": "url"
  };

  const sortFieldLabels = {
    "MESSAGE QUEUE": {
      create_date: "Date",
      message_name: "Message",
      recipients: "Recipients",
      state: "Status",
      sent_date: "Sent Date",
    },
    "MESSAGES": {
      name: "Name",
    },
  };

  // Message queue state mappings
  const queueStateMap = {
    'Q': { text: 'Queued', class: 'bg-yellow-100 text-yellow-800' },
    'S': { text: 'Sent', class: 'bg-green-100 text-green-800' },
    'PS': { text: 'Partially Sent', class: 'bg-blue-100 text-blue-800' },
    'ING': { text: 'Sending', class: 'bg-indigo-100 text-indigo-800' },
    'I': { text: 'Invalid Recipients', class: 'bg-orange-100 text-orange-800' },
    'F': { text: 'Failed', class: 'bg-red-100 text-red-800' },
    'C': { text: 'Cancelled', class: 'bg-gray-100 text-gray-800' }
  };
  // Separate pagination states for each tab
  const [paginationState, setPaginationState] = useState({
    "MESSAGE QUEUE": { currentPage: 1 },
    "MESSAGES": { currentPage: 1 },
    "MESSAGES TEMPLATE": { currentPage: 1 }
  });
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [showPageSizeMenu, setShowPageSizeMenu] = useState(false);
  const pageSizeMenuRef = useRef(null);
  const latestRequestIdRef = useRef(0);
  const activeTabRef = useRef("MESSAGE QUEUE");
  const currentTabPage = paginationState[selectedTab]?.currentPage || 1;

  const sortCycleByTab = {
    "MESSAGE QUEUE": ["create_date", "message_name"],
    "MESSAGES": ["name"],
  };
  
  const [selectedItems, setSelectedItems] = useState([]);
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);
  const allSelected = selectedItems.length === data.length;
  const [showCompose, setShowCompose] = useState(false);
  const composePrefill = useRef({});
  const getRecipientDetails = (item) => {
    if (Array.isArray(item?.recipient_details) && item.recipient_details.length > 0) {
      return item.recipient_details;
    }
    if (typeof item?.recipients === "string" && item.recipients.trim()) {
      return item.recipients
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
        .map((recipient) => ({ recipient, state: item?.state }));
    }
    return [];
  };

  // Helper function to update page for current tab
  const setCurrentPage = (page) => {
    setPaginationState(prev => ({
      ...prev,
      [selectedTab]: { ...prev[selectedTab], currentPage: page }
    }));
  };

  // Get current page for active tab
  const getCurrentPage = () => paginationState[selectedTab].currentPage;

  const getSortFieldLabel = () => {
    const labelMap = sortFieldLabels[selectedTab] || {};
    return labelMap[sortField] || sortField || "Name";
  };

  const getRangeStart = () => {
    if (!totalItems) return 0;
    return (getCurrentPage() - 1) * itemsPerPage + 1;
  };

  const getRangeEnd = () => {
    if (!totalItems) return 0;
    return Math.min(getCurrentPage() * itemsPerPage, totalItems);
  };

  const handleSummarySortFieldClick = () => {
    const options = sortCycleByTab[selectedTab] || [];
    if (options.length <= 1) {
      setOpenFilterPanel(selectedTab);
      return;
    }
    const currentIndex = options.indexOf(sortField);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % options.length;
    setSortField(options[nextIndex]);
    setCurrentPage(1);
  };

  const handleSummarySortOrderClick = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setCurrentPage(1);
  };

  const handleSummaryRangeClick = () => {
    setShowPageSizeMenu((prev) => !prev);
  };

  const handleBulkAction = async (action) => {
    try {
      const selectedMessageQueueIds = selectedItems.map(index => data[index].message_queue_id);
      const response = await API.post('/api/admin/messages/queue/bulk-action', {
        action,
        messageQueueIds: selectedMessageQueueIds
      });
      const msg = response?.data?.message;
      if (msg) showSuccess(msg);
      
      // Clear selection and refresh data
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      console.error('Error performing bulk action:', err);
      const serverMsg = err?.response?.data?.message;
      showError(serverMsg || 'Failed to perform action. Please try again.');
    }
  };

  const handleEditMessage = (item) => {
    setSelectedMessage(item);   // store clicked row data
    setIsFormOpen(true);        // open the form
  };

  const handleMessageQueue = async (item) => {
    try {
      const messageId = item?.message_id;
      let enriched = { ...item };
      if (messageId) {
        const detailRes = await API.get(`/api/admin/messages/${messageId}`);
        const detail = detailRes?.data?.data || {};
        enriched = {
          ...enriched,
          subject: detail?.subject || item?.message_name || "",
          body: detail?.body || "",
        };
      }
      setSelectedMessage(enriched);
      setIsFormOpen(true);
    } catch (err) {
      console.error("Failed to load message details:", err);
      setSelectedMessage({ ...item, subject: item?.message_name || "", body: "" });
      setIsFormOpen(true);
    }
  };

  const handleCloseForm = (shouldRefresh = false) => {
    setIsFormOpen(false);
    setSelectedMessage(null);
    if (shouldRefresh) {
      fetchData();
    }
  };

  const handleOpenTemplateForm = (templateId) => {
    setSelectedTemplateId(templateId || null);
    setIsTemplateFormOpen(true);
  };

  const handleCloseTemplateForm = () => {
    setIsTemplateFormOpen(false);
    setSelectedTemplateId(null);
  };

  useEffect(() => {
    setIsActionBarVisible(selectedItems.length > 0);
  }, [selectedItems]);

  useEffect(() => {
    // Reset sort field when changing tabs
    if (selectedTab) {
      setSortField(defaultSortFields[selectedTab]);
    }
    activeTabRef.current = selectedTab;
    setData([]);
    setTotalItems(0);
    setSelectedItems([]);
    setShowPageSizeMenu(false);
    if (selectedTab === "MESSAGES TEMPLATE") {
      filterPanel.close();
    }
  }, [selectedTab, filterPanel.close]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (pageSizeMenuRef.current && !pageSizeMenuRef.current.contains(event.target)) {
        setShowPageSizeMenu(false);
      }
    };
    if (showPageSizeMenu) {
      document.addEventListener("mousedown", handleOutside);
    }
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showPageSizeMenu]);

  useEffect(() => {
    fetchData();
  }, [selectedTab, searchTerm, sortField, sortOrder, currentTabPage, queueFilter, mqMessageType, mqFromDate, mqToDate, itemsPerPage, refreshTick]);

  useEffect(() => {
    const fetchQueueFilterOptions = async () => {
      if (selectedTab !== "MESSAGE QUEUE" || !filterPanel.isOpen) return;
      try {
        const response = await API.get("/api/admin/messages/queue/filter-options");
        const dbTypes = response?.data?.data?.messageTypes;
        if (Array.isArray(dbTypes) && dbTypes.length > 0) {
          setMqMessageTypeOptions(["All", ...dbTypes]);
        }
      } catch (err) {
        console.warn("Failed to fetch message queue filter options, using defaults", err);
      }
    };
    fetchQueueFilterOptions();
  }, [selectedTab, filterPanel.isOpen, refreshTick]);

  const fetchData = async () => {
    const requestId = ++latestRequestIdRef.current;
    const requestTab = selectedTab;

    setLoading(true);
    setError(null);
    try {
      const currentPage = paginationState[requestTab]?.currentPage || 1;
      let endpoint = "";
      switch(requestTab) {
        case "MESSAGE QUEUE":
          endpoint = `/api/admin/messages/queue?search=${encodeURIComponent(searchTerm)}&sort=${encodeURIComponent(sortField)}&order=${encodeURIComponent(sortOrder)}&page=${currentPage}&limit=${itemsPerPage}`;
          if (queueFilter && queueFilter !== 'all') endpoint += `&state=${encodeURIComponent(queueFilter)}`;
          if (mqMessageType && mqMessageType !== 'all') endpoint += `&messageType=${encodeURIComponent(mqMessageType)}`;
          if (mqFromDate) endpoint += `&fromDate=${encodeURIComponent(mqFromDate)}`;
          if (mqToDate) endpoint += `&toDate=${encodeURIComponent(mqToDate)}`;
          break;
        case "MESSAGES":
          endpoint = `/api/admin/messages?search=${searchTerm}&sort=${sortField}&order=${sortOrder}&page=${currentPage}&limit=${itemsPerPage}`;
          break;
        case "MESSAGES TEMPLATE":
          endpoint = `/api/admin/messages/templates?search=${searchTerm}&sort=${sortField}&order=${sortOrder}&page=${currentPage}&limit=${itemsPerPage}`;
          break;
        // case "WEBHOOKS":
        //   endpoint = `/api/admin/webhooks?search=${searchTerm}&sort=${sortField}&order=${sortOrder}&page=${currentPage}&limit=${itemsPerPage}`;
        //   break;
      }

      const response = await API.get(endpoint);

      if (requestId !== latestRequestIdRef.current || requestTab !== activeTabRef.current) {
        return;
      }

      setData(response.data.data);
      setTotalItems(response.data.total || response.data.data.length);
    } catch (err) {
      if (requestId !== latestRequestIdRef.current || requestTab !== activeTabRef.current) {
        return;
      }
      setError("Failed to fetch data. Please try again.");
      console.error("Error fetching data:", err);
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map((_, index) => index));
    }
  };

  const toggleSelectOne = (index) => {
    if (selectedItems.includes(index)) {
      setSelectedItems(selectedItems.filter((i) => i !== index));
    } else {
      setSelectedItems([...selectedItems, index]);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it with desc order
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    fetchData();
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!(await showConfirm("Are you sure you want to delete this template?"))) {
      return;
    }

    try {
      await API.delete(`/api/admin/messages/templates/${templateId}`);
      fetchData(); // Refresh the list
    } catch (err) {
      console.error("Error deleting template:", err);
      showError("Failed to delete template. Please try again.");
    }
  };

  // const handleDeleteWebhook = async (webhookId) => {
  //   if (!window.confirm("Are you sure you want to delete this webhook?")) {
  //     return;
  //   }

  //   try {
  //     await API.delete(`/api/admin/webhooks/${webhookId}`);
  //     fetchData(); // Refresh the list
  //   } catch (err) {
  //     console.error("Error deleting webhook:", err);
  //     alert("Failed to delete webhook. Please try again.");
  //   }
  // };

  const mqStatusLabelMap = {
    all: "All",
    S: "Sent",
    PS: "Partially Sent",
    ING: "Sending",
    Q: "Queued",
    C: "Canceled",
    I: "Invalid Recipients",
    F: "Failed",
  };

  const getMessageQueueFilterChips = () => {
    if (selectedTab !== "MESSAGE QUEUE") return [];
    const chips = [];
    if (queueFilter && queueFilter !== "all") {
      chips.push({ key: "status", label: `Status: ${mqStatusLabelMap[queueFilter] || queueFilter}` });
    }
    if (mqMessageType && mqMessageType !== "all") {
      chips.push({ key: "messageType", label: `Message Type: ${mqMessageType}` });
    }
    if (mqFromDate) chips.push({ key: "from", label: `From: ${mqFromDate}` });
    if (mqToDate) chips.push({ key: "to", label: `To: ${mqToDate}` });
    return chips;
  };

  const clearMessageQueueChip = (key) => {
    if (key === "status") setQueueFilter("all");
    if (key === "messageType") setMqMessageType("all");
    if (key === "from") setMqFromDate("");
    if (key === "to") setMqToDate("");
  };

  const queueChips = buildAdminMessagesQueueFilterChips({
    queueFilter,
    mqMessageType,
    mqFromDate,
    mqToDate,
    sortOrder,
  });

  const messagesChips = buildAdminMessagesFilterChips({
    sortOrder,
  });

  const messagesFilterPanelOpen =
    filterPanel.isOpen &&
    (selectedTab === "MESSAGE QUEUE" || selectedTab === "MESSAGES");

  return (
    <Layout active="Messages">
      <fieldset
        disabled={adminReadOnly}
        className="border-0 m-0 p-0 min-w-0 min-h-0"
      >
      <div className="py-3 px-2 flex flex-col min-h-screen"> 
        <AtlasPageHeader breadcrumb="Admin" title="Messages" showBack={false} className="mb-2" />
        <AtlasPageDivider />

        <div className="mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full flex flex-col">
          {/* Tabs */}
            <AtlasTabBar
              tabs={["MESSAGE QUEUE", "MESSAGES", "MESSAGES TEMPLATE"]}
              activeTab={selectedTab}
              onTabChange={(tab) => {
                setSelectedTab(tab);
                setSelectedItems([]);
              }}
            />

            <div
              className={`flex-1 flex flex-col min-h-0 min-w-0 ${
                messagesFilterPanelOpen ? "atlas-filter-content-offset" : ""
              }`}
            >
              {selectedTab === "MESSAGE QUEUE" && (
                <AtlasListToolbar
                  searchValue={searchTerm}
                  onSearchChange={(value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                  }}
                  searchPlaceholder="Search message queue..."
                  onFilterClick={filterPanel.toggle}
                  filterOpen={filterPanel.isOpen}
                  onRefresh={fetchData}
                  refreshSpinning={loading}
                  resultsMeta={{
                    startRow: getRangeStart(),
                    endRow: getRangeEnd(),
                    totalCount: totalItems,
                    pageSize: itemsPerPage,
                    pageSizeOptions: [10, 20, 50, 100, 200],
                    onPageSizeChange: (size) => {
                      setItemsPerPage(size);
                      setCurrentPage(1);
                    },
                    sortBy: sortField,
                    sortOrder: sortOrder,
                    sortOptions: QUEUE_SORT_OPTIONS,
                    onSortByChange: (value) => {
                      setSortField(value);
                      setCurrentPage(1);
                    },
                    onSortOrderToggle: () => {
                      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                      setCurrentPage(1);
                    },
                  }}
                  actions={
                    <button
                      type="button"
                      onClick={() => setShowCompose(true)}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                    >
                      Compose
                    </button>
                  }
                  chipBar={
                    queueChips.length > 0 ? (
                      <AtlasFilterChipBar
                        chips={queueChips}
                        onChipClick={(chip) => {
                          if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                          else filterPanel.open();
                        }}
                        onRemove={(chip) => clearMessageQueueChip(chip.key)}
                        onClearAll={() => {
                          setQueueFilter("all");
                          setMqMessageType("all");
                          setMqFromDate("");
                          setMqToDate("");
                        }}
                      />
                    ) : null
                  }
                />
              )}

              {selectedTab === "MESSAGES" && (
                <AtlasListToolbar
                  searchValue={searchTerm}
                  onSearchChange={(value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                  }}
                  searchPlaceholder="Search messages..."
                  onFilterClick={filterPanel.toggle}
                  filterOpen={filterPanel.isOpen}
                  onRefresh={fetchData}
                  refreshSpinning={loading}
                  resultsMeta={{
                    startRow: getRangeStart(),
                    endRow: getRangeEnd(),
                    totalCount: totalItems,
                    pageSize: itemsPerPage,
                    pageSizeOptions: [10, 20, 50, 100, 200],
                    onPageSizeChange: (size) => {
                      setItemsPerPage(size);
                      setCurrentPage(1);
                    },
                    sortBy: sortField,
                    sortOrder: sortOrder,
                    sortOptions: MESSAGES_SORT_OPTIONS,
                    onSortByChange: (value) => {
                      setSortField(value);
                      setCurrentPage(1);
                    },
                    onSortOrderToggle: () => {
                      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                      setCurrentPage(1);
                    },
                  }}
                  actions={
                    <button
                      type="button"
                      onClick={() => setShowCompose(true)}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                    >
                      Compose
                    </button>
                  }
                  chipBar={
                    messagesChips.length > 0 ? (
                      <AtlasFilterChipBar
                        chips={messagesChips}
                        onChipClick={(chip) => {
                          if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                          else filterPanel.open();
                        }}
                        onRemove={() => {}}
                        onClearAll={() => {
                          setSortField(defaultSortFields["MESSAGES"]);
                          setSortOrder("desc");
                        }}
                      />
                    ) : null
                  }
                />
              )}

              {selectedTab === "MESSAGES TEMPLATE" && (
                <AtlasListToolbar
                  searchValue={searchTerm}
                  onSearchChange={(value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                  }}
                  searchPlaceholder="Search templates..."
                  showFilter={false}
                  onRefresh={fetchData}
                  refreshSpinning={loading}
                  actions={
                    <button
                      type="button"
                      onClick={() => handleOpenTemplateForm(null)}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap bg-sidebar-active hover:bg-red-600"
                    >
                      CREATE
                    </button>
                  }
                />
              )}

            {/* Table for Message Queue */}
            {selectedTab === "MESSAGE QUEUE" && (
              <div className="overflow-x-auto table-force-rows no-mobile-table-stack">
                <table className="min-w-full border rounded-lg overflow-hidden shadow-lg">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr>
                    <th className="px-4 py-3 text-center w-[2%] text-sm font-medium">
                      <button
                        onClick={toggleSelectAll}
                        className={`w-5 h-5 flex items-center justify-center border rounded-sm transition 
                          ${allSelected ? "bg-[#DC1F00] border-[#DC1F00]" : "border-[#00000080] bg-white"}`}
                      >
                        {allSelected && <FaCheck className="text-white text-xs" />}
                      </button>
                    </th>
                    <th className="px-6 py-3 w-[25%] text-sm font-medium text-left">
                      <div className="flex items-center gap-2">
                        <span>Message</span>
                        <SortArrow colKey="message_name" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} />
                      </div>
                    </th>
                    <th className="text-sm font-medium px-4 py-3 w-[30%] text-left">
                      <span>Recipients</span>
                      {/* <SortArrow colKey="recipients" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} /> */}
                    </th>
                    <th className="text-sm font-medium px-4 py-3 w-[9%] text-left">
                      <span>Status</span>
                      {/* <SortArrow colKey="state" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} /> */}
                    </th>
                    <th className="text-sm font-medium px-4 py-3 rounded-tr-lg w-[20%] text-left">
                      <div className="flex items-center gap-2">
                        <span>Date</span>
                        <SortArrow colKey="create_date" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">Loading...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-red-500">{error}</td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">No messages found</td>
                    </tr>
                  ) : data.map((item, index) => {
                    const isSelected = selectedItems.includes(index);
                    return (
                      <tr 
                        key={item.message_queue_id ?? index} 
                        className={`text-sm border-b border-gray-200 transition hover:bg-orange-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleSelectOne(index)}
                            className={`w-5 h-5 flex items-center justify-center border rounded-sm transition 
                              ${isSelected ? "bg-[#DC1F00] border-[#DC1F00]" : "border-[#00000080]"}`}
                          >
                            {isSelected && <FaCheck className="text-white text-xs" />}
                          </button>
                        </td>
                        <td 
                          onClick={() => handleMessageQueue(item)}
                          className="px-6 py-3 cursor-pointer text-[#DC1F00]"
                        >
                          {item.message_name}
                        </td>
                        <td className="pl-4 pt-2 pb-1 text-xs text-textGray">
                          <div className="space-y-1">
                            {getRecipientDetails(item).length === 0 ? (
                              <span>-</span>
                            ) : (
                              getRecipientDetails(item).map((rec, recIdx) => (
                                <div key={`${item.message_queue_id}-${rec.recipient}-${recIdx}`} className="flex items-center gap-1">
                                  <span>{rec.recipient}</span>
                                  {rec.state === "S" ? (
                                    <FaCheckCircle size={14} className="text-green-500" />
                                  ) : rec.state === "F" || rec.state === "I" ? (
                                    <FaExclamationCircle size={14} className="text-red-500" />
                                  ) : null}
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="pl-4 pt-2 pb-1">
                          <span className={`px-2 py-1 rounded text-xs ${queueStateMap[item.display_state || item.state]?.class || 'bg-gray-100 text-gray-800'}`}>
                            {queueStateMap[item.display_state || item.state]?.text || 'Unknown'}
                          </span>
                        </td>
                        <td className="pl-4 pt-2 pb-1">
                          {formatAppIntlLabel(item.create_date, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}

            {/* Table for messages */}
            {selectedTab === "MESSAGES" && (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow table-force-rows no-mobile-table-stack">
                <table className="min-w-full">
                  <thead className="bg-[#dc1f0080] text-white">
                    <tr className="text-left select-none">
                      <th className="text-sm font-medium px-4 py-2 w-1/3">
                        <div className="flex items-center gap-2">
                          <span>Name</span>
                          <SortArrow colKey="name" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} />
                        </div>
                      </th>
                      <th className="text-sm font-medium px-4 py-2 w-1/5">
                        <span>Email Format</span>
                        {/* <SortArrow colKey="format" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} /> */}
                      </th>
                      <th className="text-sm font-medium px-4 py-2 w-1/5">
                        <span>Email Message Template</span>
                        {/* <SortArrow colKey="template_name" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} /> */}
                      </th>
                      <th className="text-sm font-medium px-4 py-2 w-1/5">
                        <span>Customizes Message</span>
                        {/* <SortArrow colKey="customized" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} /> */}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-2 text-sm text-center">Loading...</td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-2 text-sm text-red-500 text-center">{error}</td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-2 text-sm text-center">No messages found</td>
                      </tr>
                    ) : data.map((item, index) => (
                      <tr key={item.message_id ?? `${item.name || 'message'}-${index}`} className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td 
                          className="px-4 py-2.5 font-medium cursor-pointer text-[#DC1F00]"
                          onClick={() => handleEditMessage(item)}
                        >
                          {item.name}</td>
                        <td className="px-4 py-2.5 text-sm">{item.format}</td>
                        <td className="px-4 py-2.5 text-sm">{item.template_name || "None"}</td>
                        <td className="px-4 py-2.5 text-sm">{item.customized}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {isFormOpen && (
                  <EditMessageForm
                    message={selectedMessage}
                    onClose={handleCloseForm}
                  />
                )}
              </div>
            )}

            {/* Table for MESSAGES TEMPLATE */}
            {selectedTab === "MESSAGES TEMPLATE" && (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow no-mobile-table-stack">
                <table className="min-w-full">
                  <thead className="bg-[#dc1f0080] text-white">
                    <tr className="text-left select-none">
                      <th className="text-sm font-medium px-4 py-2">
                        <span>Name</span>
                        {/* <SortArrow colKey="name" activeKey={sortField} activeDir={sortOrder} onChange={handleSort} /> */}
                      </th>
                      <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="2" className="px-4 py-2 text-sm text-center">Loading...</td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="2" className="px-4 py-2 text-sm text-red-500 text-center">{error}</td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td colSpan="2" className="px-4 py-2 text-sm text-center">No templates found</td>
                      </tr>
                    ) : data.map((item, index) => (
                      <tr key={item.message_template_id ?? item.name ?? index} className={`text-left text-base hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td
                          className="px-4 py-2.5 font-medium cursor-pointer text-[#DC1F00]"
                          onClick={() => handleOpenTemplateForm(item.message_template_id)}
                        >
                          {item.name}
                        </td>
                        <td className="py-2.5 pr-2">
                          <ActionDropdown
                            actions={[
                              { label: "Delete", onClick: () => handleDeleteTemplate(item.message_template_id) }
                            ]} 
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Table for WEBHOOKS
            {selectedTab === "WEBHOOKS" && (
              <table className="min-w-full border shadow">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left">
                    <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg w-1/2">URL</th>
                    <th className="border text-sm font-medium px-4 py-3 w-1/2">Created</th>
                    <th className="border text-sm font-medium px-4 py-3 rounded-tr-lg w-[3%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="3" className="text-center py-4">Loading...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="3" className="text-center py-4 text-red-500">{error}</td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-4">No webhooks found</td>
                    </tr>
                  ) : data.map((item) => (
                    <tr key={item.webhook_id} className="text-left text-sm">
                      <td className="pl-4 pt-2 pb-1">{item.url}</td>
                      <td className="pl-4 pt-2 pb-1">
                        {formatAppIntlLabel(item.create_date, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        })}
                      </td>
                      <td className="py-4 pt-2 pb-1">
                        <ActionDropdown
                          actions={[
                            { label: "Delete", onClick: () => handleDeleteWebhook(item.webhook_id) }
                          ]} 
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )} */}

            {/* Pagination controller */}
            {(selectedTab === "MESSAGE QUEUE" || selectedTab === "MESSAGES" || selectedTab === "MESSAGES TEMPLATE" ) && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-auto text-sm text-muted-foreground pt-4">
                <span>
                  Showing {Math.min(itemsPerPage * (getCurrentPage() - 1) + 1, totalItems)} - {Math.min(itemsPerPage * getCurrentPage(), totalItems)} of {totalItems} items
                </span>
                <div className="flex items-center gap-1 sm:gap-2 justify-between sm:justify-end w-full sm:w-auto">
                  <button
                    className="px-3 py-2 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
                    disabled={getCurrentPage() === 1}
                    onClick={() => setCurrentPage(getCurrentPage() - 1)}
                  >
                    Previous
                  </button>

                  <div className="hidden md:flex items-center gap-1 sm:gap-2">
                    {(() => {
                      const totalPages = Math.ceil(totalItems / itemsPerPage);
                      const visiblePages = [];
                      const currentPage = getCurrentPage();
                      
                      if (totalPages <= 5) {
                        // Show all pages if 5 or fewer
                        for (let i = 1; i <= totalPages; i++) {
                          visiblePages.push(i);
                        }
                      } else {
                        // Complex pagination logic for many pages
                        if (currentPage <= 3) {
                          visiblePages.push(1, 2, 3, '...', totalPages);
                        } else if (currentPage >= totalPages - 2) {
                          visiblePages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
                        } else {
                          visiblePages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                        }
                      }

                      return visiblePages.map((page, idx) =>
                        page === '...' ? (
                          <span key={idx} className="px-3 py-2">...</span>
                        ) : (
                          <button
                            key={idx}
                            className={`px-3 py-2 border border-[#DC1F0033] rounded-md ${
                              currentPage === page ? "bg-[#DC1F00] text-white" : "hover:bg-gray-100"
                            }`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        )
                      );
                    })()}
                  </div>

                  <span className="md:hidden px-2 text-xs">
                    Page {getCurrentPage()} of {Math.ceil(totalItems / itemsPerPage)}
                  </span>

                  <button
                    className={`px-3 py-2 rounded-md ${
                      getCurrentPage() === Math.ceil(totalItems / itemsPerPage) 
                      ? 'text-gray-400 border border-[#DC1F0033]' 
                      : 'bg-buttonRed text-white hover:bg-buttonRed-hover'
                    }`}
                    disabled={getCurrentPage() === Math.ceil(totalItems / itemsPerPage)}
                    onClick={() => setCurrentPage(getCurrentPage() + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      <ComposeEmailModal isOpen={showCompose} onClose={() => setShowCompose(false)} prefill={composePrefill.current} />
      {isTemplateFormOpen && (
        <EditMessageTemplateForm
          templateId={selectedTemplateId}
          onClose={handleCloseTemplateForm}
          onSaved={() => fetchData()}
        />
      )}
      {isFormOpen && selectedTab === "MESSAGE QUEUE" && (
        <MessageQueueForm
          message={selectedMessage}
          onClose={handleCloseForm}
        />
      )}

      {/* Action Bar */}
      {isActionBarVisible && selectedTab === "MESSAGE QUEUE" && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {selectedItems.length} of {totalItems} Selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkAction('queue')}
                className="px-4 py-2 bg-buttonRed text-white rounded hover:bg-buttonRed-hover text-sm"
              >
                Queue
              </button>
              <button
                onClick={() => handleBulkAction('resend')}
                className="px-4 py-2 bg-buttonRed text-white rounded hover:bg-buttonRed-hover text-sm"
              >
                Resend
              </button>
              <button
                onClick={() => handleBulkAction('cancel')}
                className="px-4 py-2 bg-buttonRed text-white rounded hover:bg-buttonRed-hover text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
          <button
            onClick={() => setSelectedItems([])}
            className="px-4 py-2 border-2 border-black bg-white text-black hover:bg-gray-100 rounded text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      <AtlasFilterPanel
        isOpen={messagesFilterPanelOpen}
        onClose={filterPanel.close}
        onApply={filterPanel.close}
        onClear={() => {
          if (selectedTab === "MESSAGE QUEUE") {
            setQueueFilter("all");
            setMqMessageType("all");
            setMqFromDate("");
            setMqToDate("");
          } else if (selectedTab === "MESSAGES") {
            setSortField(defaultSortFields["MESSAGES"]);
            setSortOrder("desc");
          }
        }}
      >
        {selectedTab === "MESSAGE QUEUE" && (
          <AdminMessagesQueueFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            queueFilter={queueFilter}
            setQueueFilter={setQueueFilter}
            mqMessageType={mqMessageType}
            setMqMessageType={setMqMessageType}
            mqMessageTypeOptions={mqMessageTypeOptions}
            mqFromDate={mqFromDate}
            setMqFromDate={setMqFromDate}
            mqToDate={mqToDate}
            setMqToDate={setMqToDate}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
        )}
        {selectedTab === "MESSAGES" && (
          <AdminMessagesFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
        )}
      </AtlasFilterPanel>
      </div>
      </fieldset>
    </Layout>
  );
};

export default Messages;
